import { useEffect } from 'react'

const OVERFLOW_EPSILON_PX = 2
const SCROLL_GUTTER_PX = 64

const findTrack = (block: HTMLElement): HTMLElement | null =>
  block.querySelector<HTMLElement>('.category-carousel-track')

const findNavigation = (block: HTMLElement): HTMLElement | null =>
  block.querySelector<HTMLElement>('.category-carousel-nav')

const updateBlockState = (block: HTMLElement) => {
  const track = findTrack(block)
  if (!track) return

  const navigation = findNavigation(block)
  const hasOverflow = track.scrollWidth - track.clientWidth > OVERFLOW_EPSILON_PX

  block.classList.toggle('category-carousel-overflowing', hasOverflow)
  block.classList.toggle('category-carousel-fit', !hasOverflow)

  if (navigation) {
    navigation.style.display = hasOverflow ? '' : 'none'
    navigation.setAttribute('aria-hidden', hasOverflow ? 'false' : 'true')
  }

  if (!hasOverflow && track.scrollLeft !== 0) {
    track.scrollLeft = 0
  }
}

const scrollTrack = (track: HTMLElement, direction: -1 | 1) => {
  const distance = Math.max(track.clientWidth - SCROLL_GUTTER_PX, track.clientWidth * 0.75)
  track.scrollBy({
    left: direction * distance,
    behavior: 'smooth',
  })
}

export const useCategoryCarouselEnhancer = (layoutVersion: unknown) => {
  useEffect(() => {
    const blocks = Array.from(document.querySelectorAll<HTMLElement>('.category-carousel-block'))
    if (!blocks.length) return undefined

    const cleanups: Array<() => void> = []
    const updateAll = () => {
      blocks.forEach(updateBlockState)
    }

    blocks.forEach((block) => {
      const track = findTrack(block)
      if (!track) return

      const previous = block.querySelector<HTMLAnchorElement>('.category-carousel-prev')
      const next = block.querySelector<HTMLAnchorElement>('.category-carousel-next')

      const onPreviousClick = (event: MouseEvent) => {
        event.preventDefault()
        scrollTrack(track, -1)
      }
      const onNextClick = (event: MouseEvent) => {
        event.preventDefault()
        scrollTrack(track, 1)
      }

      previous?.addEventListener('click', onPreviousClick)
      next?.addEventListener('click', onNextClick)

      cleanups.push(() => {
        previous?.removeEventListener('click', onPreviousClick)
        next?.removeEventListener('click', onNextClick)
      })
    })

    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(updateAll)
        : null
    resizeObserver?.observe(document.documentElement)
    blocks.forEach((block) => {
      resizeObserver?.observe(block)
      const track = findTrack(block)
      if (track) resizeObserver?.observe(track)
    })

    window.addEventListener('resize', updateAll)
    const frame = window.requestAnimationFrame(updateAll)

    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', updateAll)
      resizeObserver?.disconnect()
      cleanups.forEach((cleanup) => cleanup())
    }
  }, [layoutVersion])
}
